
import React, { createContext, useContext, useState } from 'react';
import { FeatureConfig, FeatureKey } from '@/types/featureConfig';
import { logger } from '@/utils/logger';

// Default configuration
const defaultConfig: FeatureConfig = {
  contentScoring: {
    enabled: true,
    eeatWeight: 40,
    readabilityWeight: 30,
    technicalSeoWeight: 30
  },
  relatedArticles: {
    enabled: true,
    matchingStrength: 75,
    maxResults: 5,
    enableCache: true
  },
  faqAnalysis: {
    enabled: true,
    sensitivity: 70,
    minConfidence: 60
  }
};

// Create context
const FeatureConfigContext = createContext<{
  config: FeatureConfig;
  updateFeatureConfig: <K extends FeatureKey>(
    key: K,
    updates: Partial<FeatureConfig[K]>
  ) => void;
  resetConfig: () => void;
}>({
  config: defaultConfig,
  updateFeatureConfig: () => {},
  resetConfig: () => {}
});

// Hook for easy context use
export const useFeatureConfig = () => useContext(FeatureConfigContext);

// Provider component
export const FeatureConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<FeatureConfig>(defaultConfig);

  // Note: We're no longer loading/saving to localStorage since we're using hardcoded values

  const updateFeatureConfig = <K extends FeatureKey>(
    key: K,
    updates: Partial<FeatureConfig[K]>
  ) => {
    // Still allow UI updates for testing but don't persist them
    setConfig(prevConfig => ({
      ...prevConfig,
      [key]: {
        ...prevConfig[key],
        ...updates
      }
    }));
    
    logger.info(`Feature config updated for ${key} (not persisted)`);
  };

  const resetConfig = () => {
    setConfig(defaultConfig);
    logger.info('Feature config reset to defaults (not persisted)');
  };

  return (
    <FeatureConfigContext.Provider value={{ config, updateFeatureConfig, resetConfig }}>
      {children}
    </FeatureConfigContext.Provider>
  );
};
